import React, { useState, useEffect } from 'react';

const MetaBalanceReductionHistory = ({ recordId, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (recordId) {
      loadHistory();
    }
  }, [recordId]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      // Determinar la URL de la API basada en el entorno
      const API_URL = process.env.NODE_ENV === 'production' 
        ? process.env.REACT_APP_API_URL || 'https://dashboard.doctorweb.top/api'
        : (process.env.REACT_APP_API_URL || 'http://localhost:3001/api');

      const response = await fetch(`${API_URL}/meta-balance-reduction/${recordId}/history`);
      
      if (response.ok) {
        const data = await response.json();
        setHistory(data || []);
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setError('Error al cargar el historial: ' + error.message);
      setHistory([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Fecha no disponible';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    const num = parseFloat(amount);
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD'
    }).format(num);
  };

  const getActionIcon = (actionType) => {
    const icons = {
      created: '➕',
      updated: '✏️',
      status_changed: '🔄',
      comment_added: '💬',
      completed: '✅',
      cancelled: '❌',
      balance_updated: '💰',
      reduction_analyzed: '📊'
    };
    return icons[actionType] || '📝';
  };

  const getActionLabel = (actionType) => {
    const labels = {
      created: 'Registro creado',
      updated: 'Registro actualizado',
      status_changed: 'Estado cambiado',
      comment_added: 'Comentario agregado',
      completed: 'Marcado como completado',
      cancelled: 'Marcado como cancelado',
      balance_updated: 'Saldo actualizado',
      reduction_analyzed: 'Reducción analizada'
    };
    return labels[actionType] || actionType;
  };

  const getActionColor = (actionType) => {
    const colors = {
      created: 'bg-green-100 text-green-800 border-green-200',
      updated: 'bg-blue-100 text-blue-800 border-blue-200',
      status_changed: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      comment_added: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-teal-100 text-teal-800 border-teal-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      balance_updated: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      reduction_analyzed: 'bg-orange-100 text-orange-800 border-orange-200'
    };
    return colors[actionType] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getReductionSeverity = (diferencia) => {
    const amount = parseFloat(diferencia) || 0;
    if (amount >= 1000) {
      return { class: 'text-red-600 font-bold', icon: '🔴', label: 'Crítica' };
    } else if (amount >= 500) {
      return { class: 'text-orange-600 font-semibold', icon: '🟠', label: 'Alta' };
    } else if (amount >= 100) {
      return { class: 'text-yellow-600 font-medium', icon: '🟡', label: 'Media' };
    } else {
      return { class: 'text-green-600', icon: '🟢', label: 'Baja' };
    }
  };

  const renderHistoryItem = (item, index) => {
    const isLast = index === history.length - 1;
    
    return (
      <div key={item.id} className="relative">
        {/* Línea de conexión */}
        {!isLast && (
          <div className="absolute left-6 top-16 w-0.5 h-full bg-gradient-to-b from-green-300 to-emerald-300"></div>
        )}
        
        {/* Contenido del item */}
        <div className="flex items-start space-x-4 pb-8">
          {/* Icono */}
          <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center text-xl border-2 ${getActionColor(item.action_type)} shadow-lg`}>
            {getActionIcon(item.action_type)}
          </div>
          
          {/* Contenido */}
          <div className="flex-1 min-w-0">
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6 hover:shadow-xl transition-shadow duration-300">
              {/* Header */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 flex items-center">
                    {getActionIcon(item.action_type)}
                    <span className="ml-2">{getActionLabel(item.action_type)}</span>
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    {formatDate(item.created_at)}
                  </p>
                </div>
                
                {item.user_name && (
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-700">
                      {item.user_name}
                    </p>
                    <p className="text-xs text-gray-500">Usuario</p>
                  </div>
                )}
              </div>

              {/* Detalles del cambio */}
              {item.field_changed && (
                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Campo modificado: <span className="text-green-600">{item.field_changed}</span>
                  </p>
                  
                  {item.previous_value && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Valor anterior:</p>
                        <p className="text-sm text-red-600 bg-red-50 p-2 rounded border">
                          {item.field_changed.includes('saldo') ? formatCurrency(item.previous_value) : item.previous_value}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-1">Nuevo valor:</p>
                        <p className="text-sm text-green-600 bg-green-50 p-2 rounded border">
                          {item.field_changed.includes('saldo') ? formatCurrency(item.new_value) : item.new_value}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Mostrar diferencia si es cambio de saldo */}
                  {item.field_changed.includes('saldo') && item.previous_value && item.new_value && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-800 mb-1">Impacto de la reducción:</p>
                      <div className="flex items-center space-x-2">
                        {(() => {
                          const diferencia = parseFloat(item.previous_value) - parseFloat(item.new_value);
                          const severity = getReductionSeverity(diferencia);
                          return (
                            <>
                              <span className={severity.class}>{severity.icon}</span>
                              <span className={`font-medium ${severity.class}`}>
                                {formatCurrency(diferencia)} ({severity.label})
                              </span>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Detalles adicionales */}
              {item.details && (
                <div className="mt-4 p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                  <p className="text-sm font-medium text-emerald-800 mb-2">Detalles:</p>
                  <p className="text-sm text-emerald-700 whitespace-pre-wrap">
                    {item.details}
                  </p>
                </div>
              )}

              {/* Información adicional para reducción de saldo */}
              {item.action_type === 'created' && (
                <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200">
                  <p className="text-sm font-medium text-green-800 mb-2">📉 Información de la reducción:</p>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Tipo:</span>
                      <span className="ml-2 font-medium text-green-700">Reducción de saldo Meta</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Categoría:</span>
                      <span className="ml-2 font-medium text-green-700">Gestión financiera</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center">
                <span className="mr-3">📋</span>
                Historial de Reducción de Saldo
              </h2>
              <p className="text-green-100 mt-1">
                Registro completo de cambios y acciones realizadas
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-lg text-gray-600">Cargando historial...</p>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">⚠️</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Error al cargar historial</h3>
              <p className="text-gray-500 mb-4">{error}</p>
              <button
                onClick={loadHistory}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors duration-200"
              >
                🔄 Reintentar
              </button>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">Sin historial disponible</h3>
              <p className="text-gray-500">
                No hay registros de cambios para esta reducción de saldo.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {/* Estadísticas del historial */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 mb-8 border border-green-200">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{history.length}</div>
                    <div className="text-sm text-gray-600">Total Eventos</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {history.filter(h => h.action_type === 'updated').length}
                    </div>
                    <div className="text-sm text-gray-600">Actualizaciones</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-purple-600">
                      {history.filter(h => h.action_type === 'comment_added').length}
                    </div>
                    <div className="text-sm text-gray-600">Comentarios</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-emerald-600">
                      {history.filter(h => h.action_type === 'status_changed').length}
                    </div>
                    <div className="text-sm text-gray-600">Cambios Estado</div>
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="relative">
                <div className="space-y-1">
                  {history.map((item, index) => renderHistoryItem(item, index))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 font-medium transition-colors duration-200"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default MetaBalanceReductionHistory;
